import { Option } from "@inrev/core";
import {
	type CSSProperties,
	type FC,
	memo,
	useEffect,
	useMemo,
	useRef,
	useSyncExternalStore,
} from "react";
import { createPortal } from "react-dom";
import { logDataToSentry } from "../../../utils/sentry";
import { cn } from "../../lib/utils";
import { _BaseButton } from "../../primitives/_BaseButton";
import { type Highlight, type PageBox, toPageBox, unionBox } from "./highlights";
import type { LayerStore } from "./layerStore";
import type { Pulse } from "./useFieldSelection";
import { useViewerIds } from "./viewerIds";

/** The hit area reaches this far past the box on every side, so a thin line of text is easy to hit. */
const HIT_PADDING_PX = 3;

type HighlightState = "selected" | "hovered" | "all";

const markClasses: Record<Highlight["kind"], Record<HighlightState, string>> = {
	text: {
		selected: "bg-brand-accent/[0.26] ring-2 ring-brand-accent/[0.26]",
		hovered: "bg-brand-accent/[0.12] outline-dashed outline-1 outline-brand-accent",
		all: "bg-brand-accent/[0.13]",
	},
	region: {
		selected: "bg-brand-accent/[0.08] outline outline-2 outline-offset-[3px] outline-brand-accent",
		hovered: "outline-dashed outline-[1.5px] outline-brand-accent",
		all: "bg-brand-accent/[0.04] outline outline-1 outline-brand-accent/[0.45]",
	},
};

const boxStyle = (box: PageBox, padding = 0): CSSProperties => ({
	left: `calc(${box.left * 100}% - ${padding}px)`,
	top: `calc(${box.top * 100}% - ${padding}px)`,
	width: `calc(${box.width * 100}% + ${padding * 2}px)`,
	height: `calc(${box.height * 100}% + ${padding * 2}px)`,
});

const selectLabel = ({ label, page }: Highlight) =>
	label ? `Select field: ${label}` : `Select highlight on page ${page}`;

type PlacedHighlight = { highlight: Highlight; state: HighlightState; boxes: PageBox[] };

type PageHighlightsProps = {
	placed: PlacedHighlight[];
	pulse: Option<Pulse & { box: PageBox }>;
	onSelect?: (fieldId: string) => void;
};

const PageHighlights: FC<PageHighlightsProps> = ({ placed, pulse, onSelect }) => {
	const ids = useViewerIds();
	return (
		<>
			{placed.flatMap(({ highlight, state, boxes }) =>
				boxes.map((box, index) => {
					const first = index === 0;
					const mark = (
						<span
							aria-hidden
							className={cn(
								"absolute rounded-[2px] transition-colors",
								markClasses[highlight.kind][state],
							)}
							style={{ inset: HIT_PADDING_PX }}
						/>
					);
					const shared = {
						className: "pointer-events-auto absolute",
						style: boxStyle(box, HIT_PADDING_PX),
						"data-testid": ids.testId("highlight"),
						"data-field-id": highlight.fieldId,
						"data-state": state,
					};
					return onSelect ? (
						<_BaseButton
							key={`${highlight.id}-${index}`}
							{...shared}
							className={cn(
								shared.className,
								"cursor-pointer rounded-[4px] outline-none focus-visible:ring-2 focus-visible:ring-bc-focus",
							)}
							aria-label={first ? selectLabel(highlight) : undefined}
							aria-hidden={first ? undefined : true}
							aria-pressed={state === "selected"}
							tabIndex={first ? 0 : -1}
							onClick={() => onSelect(highlight.fieldId)}
							trackingId={ids.trackingId("highlight")}
							{...ids.fileTracking}
						>
							{mark}
						</_BaseButton>
					) : (
						<div key={`${highlight.id}-${index}`} {...shared}>
							{mark}
						</div>
					);
				}),
			)}
			{pulse && (
				<span
					key={pulse.key}
					aria-hidden
					className="documentViewerPulse"
					style={{
						...boxStyle(pulse.box, HIT_PADDING_PX),
						animationDuration: `${pulse.durationMs}ms`,
					}}
					data-testid={ids.testId("highlight-pulse")}
				/>
			)}
		</>
	);
};

type HighlightLayerProps = {
	layers: LayerStore;
	highlights: readonly Highlight[];
	activeFieldId: Option<string>;
	hoveredFieldId: Option<string>;
	showAll: boolean;
	/** Restarts the pulse around the active field each time its key changes. */
	pulse: Option<Pulse>;
	onSelect?: (fieldId: string) => void;
};

/**
 * Draws highlights into each rendered page through a portal, positioned as fractions of the
 * page so they follow zoom without re-rendering. Only the active and hovered fields show
 * unless `showAll` is on. It reads the pages' layers from their store and is memoized, so a
 * page drawn while scrolling re-renders only this, and toolbar or find updates don't at all.
 */
export const HighlightLayer = memo<HighlightLayerProps>(function HighlightLayer({
	layers: layerStore,
	highlights,
	activeFieldId,
	hoveredFieldId,
	showAll,
	pulse,
	onSelect,
}) {
	const layers = useSyncExternalStore(layerStore.subscribe, layerStore.getSnapshot);

	// Boxes only move with the pages, so hovering or selecting a field doesn't recompute them.
	const boxesById = useMemo(() => {
		const boxes = new Map<string, PageBox[]>();
		const clamped: string[] = [];
		for (const highlight of highlights) {
			const layer = layers.get(highlight.page);
			if (!layer) continue;
			boxes.set(
				highlight.id,
				highlight.rects.map((rect) => {
					const placed = toPageBox(rect, layer.viewBox, layer.rotation);
					if (placed.clamped) clamped.push(highlight.id);
					return placed.box;
				}),
			);
		}
		return { boxes, clamped };
	}, [highlights, layers]);

	const placedByPage = useMemo(() => {
		const byPage = new Map<number, PlacedHighlight[]>();
		for (const highlight of highlights) {
			const boxes = boxesById.boxes.get(highlight.id);
			const state: Option<HighlightState> =
				highlight.fieldId === activeFieldId
					? "selected"
					: highlight.fieldId === hoveredFieldId
						? "hovered"
						: showAll
							? "all"
							: undefined;
			if (!boxes || !state) continue;
			const placed = byPage.get(highlight.page) ?? [];
			placed.push({ highlight, state, boxes });
			byPage.set(highlight.page, placed);
		}
		return byPage;
	}, [highlights, boxesById, activeFieldId, hoveredFieldId, showAll]);

	const reported = useRef(new Set<string>());
	useEffect(() => {
		const fresh = boxesById.clamped.filter((id) => !reported.current.has(id));
		if (fresh.length === 0) return;
		for (const id of fresh) reported.current.add(id);
		logDataToSentry("document-viewer-highlight-outside-page", { highlightIds: fresh });
	}, [boxesById]);

	return (
		<>
			{[...layers].map(([page, layer]) => {
				const placed = placedByPage.get(page) ?? [];
				if (placed.length === 0) return null;
				const selectedBox = unionBox(
					placed.filter(({ state }) => state === "selected").flatMap(({ boxes }) => boxes),
				);
				return createPortal(
					<PageHighlights
						placed={placed}
						pulse={Option.isSome(pulse) && selectedBox ? { ...pulse, box: selectedBox } : undefined}
						onSelect={onSelect}
					/>,
					layer.host,
					`page-${page}`,
				);
			})}
		</>
	);
});
